export const DATA_MODES224 = ['real', 'institution-demo', 'sales-demo'] as const;
export type DataMode224 = (typeof DATA_MODES224)[number];

export const dataModeLabels224: Record<DataMode224, string> = {
  real: '실서비스',
  'institution-demo': '기관 체험',
  'sales-demo': '영업 시연',
};

export const TENANT_KINDS224 = ['school', 'academy', 'library', 'public', 'direct'] as const;
export type TenantKind224 = (typeof TENANT_KINDS224)[number];

export const tenantKindLabels224: Record<TenantKind224, string> = {
  school: '학교',
  academy: '학원',
  library: '도서관',
  public: '공공기관',
  direct: '개인 가입',
};

export const TENANT_STATUSES224 = ['onboarding', 'active', 'suspended', 'closed'] as const;
export type TenantStatus224 = (typeof TENANT_STATUSES224)[number];

export const tenantStatusLabels224: Record<TenantStatus224, string> = {
  onboarding: '온보딩 중',
  active: '운영 중',
  suspended: '일시 중지',
  closed: '종료',
};

export const ONBOARDING_STEPS224 = ['contract', 'admin', 'organization', 'classroom', 'content'] as const;
export type OnboardingStep224 = (typeof ONBOARDING_STEPS224)[number];

export const onboardingStepLabels224: Record<OnboardingStep224, string> = {
  contract: '계약 · 이용 범위 확인',
  admin: '기관 관리자 지정',
  organization: '학교 · 지점 등록',
  classroom: '학급 개설',
  content: '제공 콘텐츠 범위 확인',
};

export const REPORT_AUDIENCES224 = ['family', 'teacher', 'institution', 'nexpa'] as const;
export type ReportAudience224 = (typeof REPORT_AUDIENCES224)[number];

export const reportAudienceLabels224: Record<ReportAudience224, string> = {
  family: '가족',
  teacher: '교사',
  institution: '기관',
  nexpa: 'NEXPA 본사',
};

/** Every refusal the restore endpoint can answer with, in the operator's words. */
export const restoreRefusalLabels224: Record<string, string> = {
  CHECKSUM_MISMATCH: '체크섬이 맞지 않습니다 — 전송 중 손상된 파일입니다.',
  SCHEMA_TOO_NEW: '이 백업은 현재 버전보다 최신입니다 — 되돌리면 새 항목이 사라집니다.',
  FOREIGN_INSTANCE: '다른 인스턴스의 백업입니다 — 이관이라면 명시적으로 허용해야 합니다.',
  SNAPSHOT_EMPTY: '사용자나 가족이 없는 백업입니다 — 전체를 지우는 결과가 됩니다.',
  SNAPSHOT_INVALID: '백업 파일 형식을 읽을 수 없습니다.',
  SNAPSHOT_FORMAT_UNKNOWN: 'MRM 백업 파일이 아닙니다.',
};

export function restoreRefusalLabel224(reason: string): string {
  return restoreRefusalLabels224[reason] ?? reason;
}

export interface ServiceRow224 {
  serviceId: string;
  status: 'connected' | 'pending';
  events: number | null;
  linkedUsers: number | null;
}

export const serviceLabels224: Record<string, string> = {
  mrm: 'MY real Money',
  lidia: 'LIDIA',
  bookquest: 'BookQuest',
  nexmap: 'NEXMAP',
  core: 'NEXPA Core',
};

/**
 * A service that has not connected reads `연결 대기`, never `0`. An idle service
 * and an unwired one look identical as a zero, and the operator then either
 * chases a service that was never there or ignores one that has stopped.
 */
export function serviceCountLabel224(row: ServiceRow224, value: number | null): string {
  return row.status === 'pending' || value === null ? '연결 대기' : `${value.toLocaleString('ko-KR')}`;
}

export function activationRefusalLabel224(refusal: string): string {
  if (!refusal) return '';
  if (refusal === 'TENANT_CLOSED') return '종료된 기관은 다시 열 수 없습니다.';
  const missing = refusal.startsWith('ONBOARDING_INCOMPLETE:') ? refusal.split(':')[1].split(',') : [];
  if (!missing.length) return refusal;
  return `남은 온보딩: ${missing.map((step) => onboardingStepLabels224[step as OnboardingStep224] ?? step).join(' · ')}`;
}

export function seatLabel224(used: number, seatLimit: number): string {
  if (!seatLimit) return `${used}명 (좌석 미설정)`;
  return `${used} / ${seatLimit}명`;
}

/** A report caption always states scope and mode; a bare number gets misquoted. */
export function reportCaption224(audience: ReportAudience224, dataMode: DataMode224, scope: string): string {
  return `${reportAudienceLabels224[audience]} 리포트 · ${dataModeLabels224[dataMode]} · ${scope}`;
}
